import type { Transition } from 'motion/react';

/** A CSS cubic-bézier, in the tuple form `motion` takes. */
type Bezier = [number, number, number, number];

/**
 * The onboarding motion tokens, in the units `motion` works in: seconds and
 * bézier tuples.
 *
 * Ticket 11's `--bp-duration-*` and `--bp-ease-*` in `app.css` remain the
 * source of truth — the state transitions that stay in CSS (tile colour and
 * border, focus rings, the backdrop's photo reveal) read them — and these
 * mirror those values exactly. Change one and change the other.
 */
export const DURATION = {
  press: 0.09,
  fast: 0.16,
  base: 0.26,
  slow: 0.36,
} as const;

export const EASE = {
  out: [0.16, 1, 0.3, 1],
  in: [0.55, 0, 1, 0.45],
  inOut: [0.65, 0, 0.35, 1],
} satisfies Record<string, Bezier>;

/** Rise for an element arriving in place, in px. */
export const RISE = 10;

/** Horizontal travel for a step panel leaving or arriving, in px. */
export const SLIDE = 22;

/**
 * Horizontal travel for a whole screen's content arriving in the persistent
 * card. A little further than a step panel — it is a bigger change — but still
 * a phone-sized push rather than a swipe.
 */
export const SCREEN_SLIDE = 28;

/** Gap between one staged child and the next, in seconds. */
export const STAGGER = 0.045;

/** Arriving: the long ease-out. Everything that appears uses this. */
export const enterTransition: Transition = { duration: DURATION.base, ease: EASE.out };

/** Leaving: shorter and ease-in, so an exit never holds up the arrival. */
export const exitTransition: Transition = { duration: DURATION.fast, ease: EASE.in };

/**
 * Press feedback. A stiff, near-critically-damped spring rather than a tween:
 * a tap can be released mid-flight and the scale has to turn around cleanly
 * from wherever it got to.
 */
export const pressSpring: Transition = { type: 'spring', stiffness: 900, damping: 40, mass: 0.35 };

/** The confirmation pop — a tick badge appearing. One overshoot, then settle. */
export const popSpring: Transition = { type: 'spring', stiffness: 700, damping: 18, mass: 0.6 };

/**
 * The buddy "hello": one shot, three beats — the character notices you, nods,
 * settles. Keyframed rather than sprung because the beats are the point.
 */
export const helloTransition: Transition = {
  duration: DURATION.slow,
  ease: EASE.inOut,
  times: [0, 0.35, 0.7, 1],
};
